import { BrandMark } from '@/components/BrandMark';
/**
 * Dashboard shell.
 *
 * Three views, because a GM is only ever in one of three situations:
 *
 *   setup    — no campaign yet, or making a new one
 *   ready    — a campaign exists, no session running
 *   live     — a session is running; storyboard, transcript, chat, mic
 *
 * Plus the post-session state, which is `ready` with a recap card on top.
 *
 * The shell stays thin: anything with real logic lives in session/, audio/,
 * campaign/ or api/ so it can be tested without a DOM.
 */
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  advance,
  createCampaign,
  createSession,
  endSession,
  getActiveSession,
  getBillingState,
  getMe,
  getStreamToken,
  listCampaigns,
  markDraftUsed,
  listChatMessages,
  listCheckpoints,
  listNpcLines,
  sendChatMessage,
  recordClientEvent,
  resolveNpcLine,
  userFacingError,
} from '@/api/client';
import type {
  BillingState,
  Campaign,
  ChatMode,
  Checkpoint,
  Me,
  NpcLineCue,
} from '@/api/types';
import {
  MicrophoneSession,
  type AudioSource,
  type MicState,
} from '@/audio/MicrophoneSession';
import { useAuth } from '@/auth/AuthContext';
const AdminView = lazy(() =>
  import('@/components/AdminView').then((m) => ({ default: m.AdminView })),
);
const GuideView = lazy(() =>
  import('@/components/GuideView').then((m) => ({ default: m.GuideView })),
);
const TutorialView = lazy(() =>
  import('@/components/TutorialView').then((m) => ({
    default: m.TutorialView,
  })),
);
import type { CampaignDraft } from '@/campaign/premise';
import { useCampaignDraft } from '@/campaign/useDraft';
import { loadAudioSource } from '@/components/AudioSourcePicker';
import { BillingCard } from '@/components/BillingCard';
import { CampaignSetup } from '@/components/CampaignSetup';
import { GetAccess } from '@/components/GetAccess';
import { Lightbox } from '@/components/Lightbox';
import { NarrationCard } from '@/components/NarrationCard';
import { NpcLineCards } from '@/components/NpcLineCards';
import { NextChapterIcon } from '@/components/NextChapterIcon';
import { RecapCard } from '@/components/RecapCard';
import { CampaignReference } from '@/components/CampaignReference';
const SessionHistory = lazy(() =>
  import('@/components/SessionHistory').then((m) => ({
    default: m.SessionHistory,
  })),
);
import { SessionStartup } from '@/components/SessionStartup';
import { StartSession } from '@/components/StartSession';
import type { BattleMap, Seat } from '@/api/battlemap';
import { openBattleMap } from '@/api/battlemap';
import { BattleMapBoard } from '@/components/BattleMapBoard';
import { CampaignHero } from '@/components/CampaignHero';
const FirstRun = lazy(() =>
  import('@/components/FirstRun').then((m) => ({ default: m.FirstRun })),
);
import { PlanChat } from '@/components/PlanChat';
import { PlayerMap } from '@/components/PlayerMap';
import { ProfileMenu } from '@/components/ProfileMenu';
import { WorldTruth } from '@/components/WorldTruth';
import { SignInGate } from '@/components/SignInGate';
import { branchesOf, framesFor, Storyboard } from '@/components/Storyboard';
import { CombatBoard } from '@/components/CombatBoard';
import { EncounterCue } from '@/components/EncounterCue';
import { LiveWorkspace, type LiveView } from '@/components/LiveWorkspace';
import { useEncounter } from '@/session/useEncounter';
import { AgentStatus } from '@/components/AgentStatus';
import type { AgentThinking } from '@/api/types';
import { useEventStream, type ConnectionState } from '@/session/useEventStream';
import { useGlance } from '@/session/useGlance';
import { useSessionState } from '@/session/useSession';

const previews = import.meta.env.DEV
  ? {
      CombatPreview: lazy(() =>
        import('@/dev/CombatPreview').then((m) => ({
          default: m.CombatPreview,
        })),
      ),
      PitchPreview: lazy(() =>
        import('@/dev/PitchPreview').then((m) => ({ default: m.PitchPreview })),
      ),
      LayoutPreview: lazy(() =>
        import('@/dev/LayoutPreview').then((m) => ({
          default: m.LayoutPreview,
        })),
      ),
      TutorialPreview: lazy(() =>
        import('@/dev/TutorialPreview').then((m) => ({
          default: m.TutorialPreview,
        })),
      ),
      SetupPreview: lazy(() =>
        import('@/dev/SetupPreview').then((m) => ({ default: m.SetupPreview })),
      ),
      MapPreview: lazy(() =>
        import('@/dev/MapPreview').then((m) => ({ default: m.MapPreview })),
      ),
    }
  : null;

export default function App() {
  const { status, identity, signOut } = useAuth();
  const route = useHashRoute();

  // Renders the combat board against a stored encounter so the layout can be
  // checked without waiting on a live build. Dev only — a GM has no reason to
  // reach it, and it must never ship a fake fight into a real session.
  if (previews && route === '#/pitch-preview') {
    return <previews.PitchPreview onExit={() => goTo('')} />;
  }
  if (previews && route === '#/combat-preview') {
    return <previews.CombatPreview onExit={() => goTo('')} />;
  }
  // The live layout, against fixtures. Reaching the real one needs an account, a
  // campaign, a session and a spawned agent, which is why its phone behaviour
  // went unexamined for so long.
  if (previews && route === '#/layout-preview') {
    return (
      <div className="app">
        <previews.LayoutPreview onExit={() => goTo('')} />
      </div>
    );
  }
  if (previews && route === '#/tutorial-preview') {
    return <previews.TutorialPreview />;
  }
  if (previews && route === '#/setup-preview') {
    return <previews.SetupPreview />;
  }
  if (previews && route === '#/map-preview') {
    return <previews.MapPreview />;
  }
  // Before the gate on purpose: a player opens their link with no account,
  // handed to them in a Discord call. Requiring sign-in here would break the
  // one page whose entire audience has never heard of this app.
  if (route.startsWith('#/map/')) {
    return (
      <div className="app app--player">
        <PlayerMap
          key={route}
          seatKey={decodeURIComponent(route.slice('#/map/'.length))}
        />
      </div>
    );
  }
  if (status !== 'signed-in') return <SignInGate />;
  // Every signed-in destination renders inside the dashboard rather than
  // replacing it. Two reasons, and the second is not cosmetic: the nav stays
  // put instead of being swapped for a "Back to dashboard" button on each
  // screen, and the dashboard staying mounted means its microphone survives
  // the trip. Clicking "Sessions" mid-game used to unmount the mic, which is
  // why a modal sheet existed to avoid navigating at all.
  return <Dashboard email={identity?.email ?? ''} onSignOut={signOut} />;
}

/**
 * Hash routing, deliberately without a router library.
 *
 * There are four destinations and no nested or parameterised routes. A hash
 * keeps them linkable and back-button-friendly without adding a dependency
 * or a server-side rewrite rule for deep links.
 */
/**
 * One nav item. An anchor rather than a button so it is a real link — the
 * middle-click, the copied URL and the back button all keep working, which is
 * most of the reason this app routes on the hash in the first place.
 */
function NavLink({
  href,
  label,
  current,
}: {
  href: string;
  label: string;
  current: boolean;
}) {
  return (
    <a
      className={
        current ? 'masthead__link masthead__link--on' : 'masthead__link'
      }
      href={href || '#'}
      aria-current={current ? 'page' : undefined}
    >
      {label}
    </a>
  );
}

function useHashRoute(): string {
  const [route, setRoute] = useState(() => window.location.hash);
  useEffect(() => {
    const onChange = () => setRoute(window.location.hash);
    window.addEventListener('hashchange', onChange);
    return () => window.removeEventListener('hashchange', onChange);
  }, []);
  return route;
}

function goTo(hash: string): void {
  window.location.hash = hash;
}

/** Why the session stopped, said to a GM whose players are watching them read
 *  it. Every branch has to answer the only question that matters in that
 *  moment — is my campaign gone? — so each one says what carries over. The
 *  fallback exists because the reason column is written by the agent and a new
 *  reason must never reach a real table as a bare identifier: a GM once saw
 *  "Reason: max_ticks." mid-game. */
function suspensionMessage(reason: string | null): string {
  switch (reason) {
    case 'budget_exceeded':
      return 'It reached the spend cap set for it. Raise the cap and start a new session, and Throughline picks up from the campaign as it stands.';
    case 'max_duration':
      return 'It reached the longest a single session can run. Nothing is lost. Your world, your NPCs, and everything established tonight are saved to the campaign, and a new session carries on from here.';
    default:
      return 'Your campaign is safe. The world and everything established in it are saved, and a new session picks up where this one stopped.';
  }
}

function Dashboard({
  email,
  onSignOut,
}: {
  email: string;
  onSignOut: () => void;
}) {
  const route = useHashRoute();
  const { state, dispatch, log, applyEvent } = useSessionState();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [bootState, setBootState] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  const [bootAttempt, setBootAttempt] = useState(0);
  const [selectedId, setSelectedId] = useState('');
  const [billing, setBilling] = useState<BillingState | null>(null);
  const startWithMicrophone = useRef(false);
  const [micState, setMicState] = useState<MicState>('off');
  /**
   * "Connected, but hearing silence."
   *
   * The edge already detects this — it counts speech frames on the audio socket
   * and sends a warning every fifteen seconds when none arrive. That warning
   * went into the masthead status line, which is the quietest text on the page.
   * A guest ran three sessions this way: the mic was connected, the routing was
   * wrong, two sessions recorded no audio at all, and each still cost a few
   * cents for an opening narration nobody could use. Nothing ever said so.
   */
  const [silent, setSilent] = useState<string | null>(null);
  // The Next-checkpoint press, tracked to the board's arrival. The button
  // used to fire and change nothing, and a button that appears not to work
  // gets pressed again — the measured presses behind the one-press-one-board
  // dedupe were 0.1–1.4s apart. The server dedupes; this makes pressing once
  // *feel* sufficient. 'queued' holds until checkpoint.created arrives.
  // Undefined until /me answers, so the tour cannot flash on for a moment
  // and then vanish for someone who has already done it.
  const [showTour, setShowTour] = useState<boolean | undefined>(undefined);
  const [battleMap, setBattleMap] = useState<{
    map: BattleMap;
    seats: Seat[];
  } | null>(null);
  const [advanceState, setAdvanceState] = useState<
    'idle' | 'sending' | 'queued'
  >('idle');
  const advanceResetTimer = useRef<number | undefined>(undefined);
  const [connection, setConnection] = useState<ConnectionState>('idle');
  const [creatingCampaign, setCreatingCampaign] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{
    checkpoint: Checkpoint;
    branchIdx: number;
  } | null>(null);
  const [reference, setReference] = useState(false);
  const [me, setMe] = useState<Me | null>(null);
  const [intent, setIntent] = useState('');
  const [audioSource, setAudioSource] = useState<AudioSource>(loadAudioSource);
  const micRef = useRef<MicrophoneSession | null>(null);
  const { draft, update, adopt, startFresh, saveState, serverId } =
    useCampaignDraft();

  const session = state.session;
  const sessionId = session?.id ?? null;
  const isLive = session?.status === 'live';

  useEffect(() => {
    setBattleMap(null);
  }, [sessionId]);

  // Board-open heartbeat: one event on open and one a minute while a live
  // session's dashboard is actually visible. This existed as plumbing for a
  // while with no call sites, which meant "did the GM ever look at the
  // board?" could only be answered by reading edge access logs.
  useEffect(() => {
    if (!isLive || !sessionId) return;
    const beat = () => {
      if (document.visibilityState === 'visible') {
        recordClientEvent('board_open', 'debug', '', {}, sessionId);
      }
    };
    beat();
    const t = window.setInterval(beat, 60_000);
    return () => window.clearInterval(t);
  }, [isLive, sessionId]);
  const endedSessionId =
    session && session.status !== 'live' ? session.id : null;
  const combat = useEncounter(isLive ? sessionId : null);
  const glance = useGlance(sessionId, isLive);
  const [thinking, setThinking] = useState<AgentThinking | null>(null);
  const [liveView, setLiveView] = useState<LiveView>('board');
  // Counted against what was on screen when the GM last looked at each pane, so
  // a phone GM on Board can see that the table said something. Reset by
  // visiting the pane, not by time.
  const seen = useRef({ heard: 0, chat: 0, npcLines: new Set<string>() });
  const unread = {
    heard: Math.max(0, state.transcript.length - seen.current.heard),
    chat: Math.max(0, state.chat.length - seen.current.chat),
    board: state.npcLines.filter((cue) => !seen.current.npcLines.has(cue.id))
      .length,
  };
  useEffect(() => {
    if (liveView === 'heard') seen.current.heard = state.transcript.length;
    if (liveView === 'chat') seen.current.chat = state.chat.length;
    if (liveView === 'board') {
      state.npcLines.forEach((cue) => seen.current.npcLines.add(cue.id));
    }
  }, [liveView, state.transcript.length, state.chat.length, state.npcLines]);

  const refreshBilling = useCallback(async () => {
    setBilling(await getBillingState().catch(() => null));
  }, []);

  const refreshCheckpoints = useCallback(async () => {
    if (!sessionId) return;
    try {
      dispatch({
        type: 'checkpoints/set',
        checkpoints: await listCheckpoints(sessionId),
      });
    } catch (err) {
      log(`The board couldn’t refresh: ${describe(err)}`, 'warn');
    }
  }, [sessionId, dispatch, log]);

  const refreshChat = useCallback(async () => {
    if (!sessionId) return;
    try {
      dispatch({
        type: 'chat/set',
        messages: await listChatMessages(sessionId),
      });
    } catch {
      /* chat is non-critical; the panel shows what it has */
    }
  }, [sessionId, dispatch]);

  const refreshNpcLines = useCallback(async () => {
    if (!sessionId) return;
    try {
      dispatch({ type: 'npc-lines/set', cues: await listNpcLines(sessionId) });
    } catch {
      /* optional live aid; the rest of the session remains usable */
    }
  }, [sessionId, dispatch]);

  // Boot: resume any running session, load campaigns and billing.
  useEffect(() => {
    let cancelled = false;
    setBootState('loading');
    void (async () => {
      try {
        const [active, list, bill, who] = await Promise.all([
          getActiveSession(),
          listCampaigns(),
          getBillingState().catch(() => null),
          getMe().catch(() => null),
        ]);
        if (cancelled) return;
        setCampaigns(list);
        setBilling(bill);
        setMe(who);
        // A failed /me is not a reason to tour someone: `who` is null on error,
        // and a spurious tour over a live dashboard would be worse than none.
        setShowTour(who ? !who.onboarded : false);
        const first = list[0];
        if (first) setSelectedId((prev) => prev || first.id);
        if (active) {
          dispatch({ type: 'session/set', session: active });
          log(`resumed session ${active.id}`, 'ok');
        }
        setBootState('ready');
      } catch (err) {
        if (!cancelled) {
          setError(describe(err));
          setBootState('error');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, log, bootAttempt]);

  useEffect(() => {
    void refreshCheckpoints();
    void refreshChat();
    void refreshNpcLines();
  }, [refreshCheckpoints, refreshChat, refreshNpcLines]);

  useEventStream({
    sessionId: isLive ? sessionId : null,
    onStateChange: setConnection,
    onEvent: (event) => {
      if (event.kind === 'session.thinking') {
        setThinking(event.data as AgentThinking);
        return;
      }
      if (event.kind === 'checkpoint.created') {
        // The press is fulfilled. Falls through on purpose — the reducer
        // still needs the event to fetch the board itself.
        window.clearTimeout(advanceResetTimer.current);
        setAdvanceState('idle');
      }
      if (
        event.kind === 'encounter.ready' ||
        event.kind === 'encounter.pitched' ||
        // A GM-requested fight goes straight to building with no pitch, so
        // this event is the only signal the dashboard gets that anything
        // started — without it the GM who just asked for a fight watches a
        // board that shows nothing for two and a half minutes.
        event.kind === 'encounter.building' ||
        // A build died and the pitch is back with the error on it. The board
        // must refetch or the GM keeps a "Building" banner over nothing —
        // which is precisely the lie this event was created to end.
        event.kind === 'encounter.failed'
      ) {
        void combat.refresh();
        return;
      }
      // canon.updated has been published and subscribed since the canon graph
      // was built, with nothing listening — so a fact the agent established
      // mid-scene reached the database and stopped there. Transcript matters
      // too: the ranker scores against the last seven minutes of speech, so
      // relevance moves as people talk with no write involved.
      if (
        event.kind === 'canon.updated' ||
        event.kind === 'transcript.appended'
      ) {
        glance.poke();
      }
      if (applyEvent(event)) void refreshCheckpoints();
    },
    onReconnect: () => {
      log('event stream reconnected', 'ok');
      glance.poke();
      void refreshCheckpoints();
      void refreshChat();
      void refreshNpcLines();
      // A fight may have been prepared while the stream was down.
      void combat.refresh();
    },
  });

  async function handleNpcLine(cue: NpcLineCue, status: 'used' | 'dismissed') {
    if (!sessionId) return;
    await resolveNpcLine(sessionId, cue.id, status);
    dispatch({ type: 'npc-lines/remove', cueId: cue.id });
  }

  // One mic per session; disposed on change so the browser's recording
  // indicator never outlives the session.
  useEffect(() => {
    if (!isLive) setThinking(null);
  }, [isLive]);

  // A queued press belongs to one session; carrying it into the next would
  // show "on the way" for a board nobody asked that session for.
  useEffect(() => {
    window.clearTimeout(advanceResetTimer.current);
    setAdvanceState('idle');
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !isLive) return;
    const mic = new MicrophoneSession(
      sessionId,
      {
        onState: setMicState,
        onMessage: (message) => {
          if (message.type === 'turn') {
            dispatch({ type: 'transcript/append', turn: message as never });
            // The caption arrives whether or not the row was written, so an
            // unpersisted turn is invisible: the transcript scrolls and the
            // agent, which reads rows, never sees the line. Say so — a GM who
            // knows the system missed the last minute can repeat the beat.
            if (message.persisted === false) {
              log(
                'One transcript line wasn’t saved, so the Showrunner missed it. Repeat anything important.',
                'warn',
              );
            }
          } else if (message.type === 'partial' && message.text) {
            // The words themselves do NOT go here. This is the masthead, and a
            // partial arrives every few hundred milliseconds — putting the live
            // text in a flex row re-measured the header on every syllable and
            // shoved the whole thing sideways for the length of a session.
            // The words already have a home in the Heard pane, which is where a
            // GM reads them. All the masthead owes them is "audio is landing".
            dispatch({ type: 'status/set', text: 'hearing the table' });
            // Words arrived, so whatever was wrong is no longer wrong.
            setSilent(null);
          } else if (message.type === 'audio_status' && message.message) {
            if (message.level === 'warn') {
              setSilent(message.message);
            } else {
              dispatch({ type: 'status/set', text: message.message });
            }
          }
        },
        onLog: (text, level) => log(text, level ?? 'info'),
      },
      {
        getStreamToken: async () => (await getStreamToken(sessionId)).token,
        source: audioSource,
      },
    );
    micRef.current = mic;

    // Starting a session requests the chosen audio input. Reopening a saved
    // session waits for the GM's mic button, so a reload cannot unexpectedly
    // capture audio or strand the workspace behind a permission prompt.
    const autoStart = window.setTimeout(() => {
      if (micRef.current === mic && startWithMicrophone.current) {
        startWithMicrophone.current = false;
        void mic.start();
      }
    }, 0);

    return () => {
      window.clearTimeout(autoStart);
      micRef.current = null;
      void mic.dispose();
    };
  }, [sessionId, isLive, audioSource, dispatch, log]);

  async function handleCreateCampaign(
    premise: string,
    slug: string,
    name: string,
    draft: CampaignDraft,
  ) {
    setBusy(true);
    setError(null);
    try {
      const campaign = await createCampaign({
        slug,
        name,
        premise,
        // Blank means the GM picked "something else" and typed nothing;
        // let the server's default stand rather than sending an empty slug.
        system_key: draft.systemKey || undefined,
        system_notes: draft.systemNotes,
        language: draft.language || 'en',
        // The party is what encounter difficulty is scored against. Sending it
        // is the difference between fights balanced for this table and fights
        // balanced for four level-3 characters, which is what the server fell
        // back to when nothing ever wrote party_members.
        players: draft.players
          .filter((p) => p.characterName.trim() || p.playerName.trim())
          .map((p) => ({
            name: p.characterName.trim() || p.playerName.trim(),
            player_name: p.playerName.trim(),
            level: p.level.trim() ? Number(p.level) : null,
            signature: [p.backstory.trim(), p.playerDetails.trim()]
              .filter(Boolean)
              .join(' — ')
              .slice(0, 600),
          })),
      });
      setCampaigns((prev) => [
        campaign,
        ...prev.filter((c) => c.id !== campaign.id),
      ]);
      setSelectedId(campaign.id);
      setCreatingCampaign(false);
      // Link the draft to what it made, then move the form to a fresh one.
      // The draft is kept either way — this only labels it in the list.
      if (serverId) {
        void markDraftUsed(serverId, campaign.id).catch(() => undefined);
      }
      startFresh();
      log(`campaign "${campaign.name}" created`, 'ok');
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleStart() {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createSession(selectedId, intent);
      startWithMicrophone.current = true;
      dispatch({ type: 'reset' });
      dispatch({ type: 'session/set', session: created });
      setIntent('');
      log(`session started`, 'ok');
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleEnd() {
    if (!sessionId) return;
    setBusy(true);
    try {
      await micRef.current?.stop({ log: false });
      dispatch({ type: 'session/set', session: await endSession(sessionId) });
      log('session ended', 'ok');
    } catch (err) {
      setError(describe(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleAdvance() {
    if (!sessionId || advanceState !== 'idle') return;
    setAdvanceState('sending');
    try {
      await advance(sessionId);
      log('asked for the next checkpoint', 'ok');
      setAdvanceState('queued');
      // A board normally lands inside two minutes. If nothing arrives, the
      // button must come back — a control stuck at "on the way" over a dead
      // request is the Building-banner lie all over again.
      window.clearTimeout(advanceResetTimer.current);
      advanceResetTimer.current = window.setTimeout(
        () => setAdvanceState('idle'),
        240_000,
      );
    } catch (err) {
      log(`The next checkpoint wasn’t requested: ${describe(err)}`, 'warn');
      setAdvanceState('idle');
    }
  }

  async function handleChat(mode: ChatMode, content: string) {
    if (!sessionId) return;
    const message = await sendChatMessage(sessionId, mode, content);
    dispatch({ type: 'chat/upsert', message });
  }

  const statusLine = useMemo(() => {
    if (state.status) return state.status;
    if (connection === 'retrying') return 'Reconnecting…';
    if (isLive) return 'Live';
    return '';
  }, [state.status, connection, isLive, email]);

  const selected = campaigns.find((c) => c.id === selectedId) ?? null;
  const sessionCampaign =
    campaigns.find((campaign) => campaign.id === session?.campaign_id) ??
    selected;

  // Which section the nav is pointing at. Unknown hashes fall back to Home
  // rather than rendering nothing, so a stale bookmark lands somewhere real.
  const view: 'home' | 'sessions' | 'tutorial' | 'guide' | 'admin' =
    route === '#/sessions'
      ? 'sessions'
      : route === '#/tutorial'
        ? 'tutorial'
        : route === '#/guide'
          ? 'guide'
          : route === '#/admin' && me?.is_admin
            ? 'admin'
            : 'home';

  return (
    <div className="app">
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          const main = document.querySelector<HTMLElement>('main');
          if (main) {
            main.tabIndex = -1;
            main.focus();
          }
        }}
      >
        Skip to content
      </a>
      <header className="masthead">
        <span className="masthead__brand">
          <BrandMark />
          Throughline <small>the GM’s companion</small>
        </span>
        {/* During a session AgentStatus owns this job. Showing both status
            strings squeezed its useful explanation into "you a…" while the
            redundant line repeated mic/connection state already visible in
            the controls and alerts. */}
        {!isLive && statusLine ? (
          <span className="masthead__status">{statusLine}</span>
        ) : null}
        {/* What the agent is doing, distinct from the mic. Sits next to the
            identity line because it is the other thing a GM checks without
            wanting to look away from the table for long. */}
        {isLive ? (
          <AgentStatus thinking={thinking} stale={connection !== 'open'} />
        ) : null}
        <span className="masthead__spacer" />

        {isLive ? (
          <div className="session-controls">
            <button
              type="button"
              className={
                micState === 'on'
                  ? 'button button--live'
                  : micState === 'starting'
                    ? 'button'
                    : 'button button--danger'
              }
              onClick={() => {
                setSilent(null);
                void micRef.current?.toggle();
              }}
              disabled={micState === 'starting'}
              aria-pressed={micState === 'on'}
            >
              <span
                className={`mic-dot mic-dot--${micState}`}
                aria-hidden="true"
              />
              {micState === 'on'
                ? 'Mic on'
                : micState === 'starting'
                  ? 'Starting…'
                  : 'Mic off'}
            </button>
            <button
              type="button"
              // Fixed width via the class: three labels of different lengths
              // in the masthead is how the "hearing" indicator made the whole
              // screen shift.
              className={
                advanceState === 'idle'
                  ? 'button button--advance'
                  : 'button button--advance button--advance-waiting'
              }
              onClick={() => void handleAdvance()}
              disabled={advanceState !== 'idle'}
              aria-live="polite"
            >
              {advanceState === 'idle' ? (
                <>
                  <NextChapterIcon />
                  Next checkpoint
                </>
              ) : (
                <>
                  <span className="spinner" aria-hidden="true" />
                  {advanceState === 'sending'
                    ? 'Requesting…'
                    : thinking?.phase === 'preparing' ||
                        thinking?.phase === 'rendering'
                      ? 'Building your board…'
                      : 'On its way…'}
                </>
              )}
            </button>
            <button
              type="button"
              className="button button--danger"
              onClick={() => void handleEnd()}
              disabled={busy}
            >
              End session
            </button>
          </div>
        ) : null}
        {sessionId ? (
          <button
            type="button"
            className="button button--quiet"
            onClick={() => setReference(true)}
          >
            Campaign
          </button>
        ) : null}
        {/* One nav, in one order, on every screen. Home is a real
            destination rather than something you get to by pressing a "Back
            to dashboard" button that only exists on some pages. */}
        <nav className="masthead__nav" aria-label="Sections">
          <NavLink
            href=""
            label={isLive ? 'Live session' : 'Home'}
            current={view === 'home'}
          />
          <NavLink
            href="#/sessions"
            label="Sessions"
            current={view === 'sessions'}
          />
          <NavLink
            href="#/tutorial"
            label="Tutorial"
            current={view === 'tutorial'}
          />
          <NavLink href="#/guide" label="GM Guide" current={view === 'guide'} />
          {me?.is_admin ? (
            <NavLink href="#/admin" label="Admin" current={view === 'admin'} />
          ) : null}
        </nav>
        <ProfileMenu email={email} onSignOut={onSignOut} />
      </header>

      <Suspense
        fallback={
          <main className="route-loading" role="status">
            <span className="spinner" />
            Opening your workspace…
          </main>
        }
      >
        {view === 'sessions' ? (
          <SessionHistory />
        ) : view === 'tutorial' ? (
          <TutorialView />
        ) : view === 'guide' ? (
          <GuideView />
        ) : view === 'admin' ? (
          <AdminView />
        ) : null}
        <div className="dashboard-home" hidden={view !== 'home'}>
          {bootState !== 'ready' ? (
            <main className="route-loading" tabIndex={-1}>
              {bootState === 'loading' ? (
                <p role="status">
                  <span className="spinner" /> Opening your campaigns…
                </p>
              ) : (
                <section className="card">
                  <h1 className="card__title">Your workspace couldn’t load</h1>
                  <p role="alert">{error}</p>
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={() => setBootAttempt((n) => n + 1)}
                  >
                    Try again
                  </button>
                </section>
              )}
            </main>
          ) : billing && billing.subscription_status !== 'active' ? (
            <main className="workspace workspace--single" tabIndex={-1}>
              <div className="pane">
                <div className="pane__body">
                  <GetAccess
                    billing={billing}
                    email={email}
                    onRedeemed={() => void refreshBilling()}
                  />
                </div>
              </div>
            </main>
          ) : creatingCampaign || campaigns.length === 0 ? (
            <main className="workspace workspace--single" tabIndex={-1}>
              <div className="pane">
                <div className="pane__body pane__body--setup">
                  <CampaignSetup
                    draft={draft}
                    saveState={saveState}
                    draftId={serverId}
                    onOpenDraft={adopt}
                    busy={busy}
                    error={error}
                    onChange={update}
                    onSubmit={(p, s, n, d) =>
                      void handleCreateCampaign(p, s, n, d)
                    }
                    {...(campaigns.length > 0
                      ? { onCancel: () => setCreatingCampaign(false) }
                      : {})}
                  />
                  {/* A fresh trial account lives on this screen until its first
                  campaign exists, and it should be able to see the allowance
                  it is spending against — this card was only on the
                  campaign-home screen, so brand-new accounts had no way to
                  see their cap at all. */}
                  <BillingCard
                    state={billing}
                    onChanged={() => void refreshBilling()}
                  />
                </div>
              </div>
            </main>
          ) : !isLive ? (
            <main className="workspace workspace--single" tabIndex={-1}>
              <div className="pane">
                <div className="pane__body ready">
                  {/* Why the session stopped, on the screen the GM lands on. A
                  suspension used to leave only one line in a scrolling log,
                  which is not where anyone looks to find out why the tool went
                  quiet. */}
                  {session?.status === 'suspended' ? (
                    <section className="card card--warn">
                      <header className="card__head">
                        <h2 className="card__title">
                          This session was stopped
                        </h2>
                      </header>
                      <p>{suspensionMessage(session.suspended_reason)}</p>
                    </section>
                  ) : null}

                  <CampaignHero campaign={selected ?? null} />

                  {endedSessionId ? (
                    <RecapCard sessionId={endedSessionId} />
                  ) : null}

                  <div className="ready-grid">
                    <StartSession
                      campaigns={campaigns}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      audioSource={audioSource}
                      onAudioSource={setAudioSource}
                      intent={intent}
                      onIntent={setIntent}
                      error={error}
                      busy={busy}
                      onStart={() => void handleStart()}
                      onNewCampaign={() => setCreatingCampaign(true)}
                    />

                    <div className="ready-side">
                      {selectedId ? <PlanChat campaignId={selectedId} /> : null}
                      {selectedId ? (
                        <WorldTruth campaignId={selectedId} />
                      ) : null}
                    </div>
                  </div>

                  <BillingCard
                    state={billing}
                    onChanged={() => void refreshBilling()}
                  />
                </div>
              </div>
            </main>
          ) : (
            <LiveWorkspace
              wideBoard={Boolean(battleMap)}
              campaignName={sessionCampaign?.name || selected?.name}
              view={liveView}
              onView={setLiveView}
              glance={glance.entities}
              campaignId={session?.campaign_id ?? selectedId ?? null}
              transcript={state.transcript}
              chat={state.chat}
              chatDisabled={!isLive}
              onSend={handleChat}
              unreadHeard={unread.heard}
              unreadChat={unread.chat}
              unreadBoard={unread.board}
              board={
                <>
                  {!battleMap ? (
                    <NpcLineCards
                      cues={state.npcLines}
                      onResolve={handleNpcLine}
                    />
                  ) : null}
                  {error || combat.error ? (
                    <div className="notice notice--error" role="alert">
                      {error || combat.error}
                    </div>
                  ) : null}
                  {battleMap ? (
                    <BattleMapBoard
                      key={battleMap.map.id}
                      map={battleMap.map}
                      seats={battleMap.seats}
                      onClose={() => setBattleMap(null)}
                    />
                  ) : null}
                  {battleMap && state.npcLines.length > 0 ? (
                    <details className="combat-dialogue">
                      <summary>
                        NPC dialogue{' '}
                        <span className="desk-count">
                          {state.npcLines.length} ready
                        </span>
                      </summary>
                      <NpcLineCards
                        cues={state.npcLines}
                        onResolve={handleNpcLine}
                      />
                    </details>
                  ) : null}
                  <div hidden={Boolean(battleMap)}>
                    {combat.encounter?.design ? (
                      <div hidden={!combat.open}>
                        <CombatBoard
                          key={combat.encounter.id}
                          design={combat.encounter.design}
                          party={combat.encounter.party_names ?? []}
                          prerolls={combat.encounter.prerolls}
                          scored={{
                            difficulty: combat.encounter.scored_difficulty,
                            adjustedXp: combat.encounter.adjusted_xp,
                            threshold: combat.encounter.party_threshold_xp,
                          }}
                          onClose={() => combat.setOpen(false)}
                          onResolve={() => void combat.resolve()}
                          onOpenBattleMap={
                            combat.encounter.design.map
                              ? async () => {
                                  if (!combat.encounter) return;
                                  try {
                                    setBattleMap(
                                      await openBattleMap(combat.encounter.id),
                                    );
                                  } catch (err) {
                                    setError(describe(err));
                                  }
                                }
                              : undefined
                          }
                        />
                      </div>
                    ) : null}
                    {!combat.open || !combat.encounter?.design ? (
                      <>
                        {/* A pitch has no design — that is the whole point of it,
                      the fight has not been built yet. Gating the cue on
                      `design` meant the pitch card, and its Build it button,
                      never rendered at all: EncounterCue handled the case and
                      nothing ever let it through. Found live, with a GM staring
                      at a board that had a fight waiting on it. */}
                        {combat.encounter?.design ||
                        combat.encounter?.pitch ||
                        // A requested fight is building with no pitch and no design
                        // yet — the gate above would hide the Building card for the
                        // whole build, which is the "where is the combat?" failure
                        // all over again by a new route.
                        combat.encounter?.status === 'building' ? (
                          <EncounterCue
                            encounter={combat.encounter}
                            onOpen={() => {
                              combat.setOpen(true);
                              void combat.begin();
                            }}
                            onAccept={() => void combat.accept()}
                            // Takes the argument. A zero-arg arrow is assignable to
                            // a one-arg prop, so dropping the refusal here would
                            // type-check cleanly and silently throw away everything
                            // the GM said about why — which is the whole point of
                            // having asked them.
                            onDiscard={(refusal) =>
                              void combat.discard(refusal)
                            }
                          />
                        ) : null}
                        {sessionId ? (
                          <SessionStartup
                            sessionId={sessionId}
                            campaign={sessionCampaign}
                            narration={state.narration}
                            checkpoints={state.checkpoints}
                          />
                        ) : null}
                        {state.narration ? (
                          <NarrationCard text={state.narration} />
                        ) : null}
                        {state.checkpoints.length > 0 ? (
                          <Storyboard
                            checkpoints={state.checkpoints}
                            building={null}
                            onOpenBranch={(checkpoint, branchIdx) =>
                              setLightbox({ checkpoint, branchIdx })
                            }
                          />
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </>
              }
            />
          )}
        </div>
      </Suspense>

      {/* The mic going off is the one failure that looks like nothing.
          Transcription stops, the agent hears nothing more, and the only sign
          was a small grey dot in the masthead — a GM lost most of a session to
          it and said afterwards "omg the mic turned off". A live session with
          the mic off gets a bar across the board and a button to fix it. */}
      {/* Connected but silent. Distinct from the mic being off: the mic is on,
          the socket is streaming, and every frame is quiet — which is what a
          wrong input device or a broken routing setup looks like, and it is
          invisible without being told. */}
      {isLive && micState === 'on' && silent ? (
        <div className="micoff micoff--silent" role="alert">
          <span className="micoff__dot" aria-hidden="true" />
          <span className="micoff__text">
            {silent} Nothing said at the table is reaching Throughline.
          </span>
          <button
            type="button"
            className="button"
            onClick={() => void micRef.current?.stop()}
          >
            Stop and pick another input
          </button>
        </div>
      ) : null}
      {isLive && micState !== 'on' ? (
        <div className="micoff" role="alert">
          <span className="micoff__dot" aria-hidden="true" />
          <span className="micoff__text">
            {micState === 'starting'
              ? 'Starting the mic…'
              : 'The mic is off. Throughline can’t hear the table.'}
          </span>
          {micState !== 'starting' ? (
            <button
              type="button"
              className="button button--primary"
              onClick={() => void micRef.current?.toggle()}
            >
              Turn the mic on
            </button>
          ) : null}
        </div>
      ) : null}
      {reference && sessionId ? (
        <CampaignReference
          sessionId={sessionId}
          onClose={() => setReference(false)}
        />
      ) : null}
      <Suspense fallback={null}>
        {showTour ? <FirstRun onDone={() => setShowTour(false)} /> : null}
      </Suspense>
      {lightbox ? (
        <Lightbox
          branch={branchesOf(lightbox.checkpoint)[lightbox.branchIdx]}
          frames={framesFor(lightbox.checkpoint, lightbox.branchIdx)}
          onClose={() => setLightbox(null)}
        />
      ) : null}
    </div>
  );
}

function describe(error: unknown): string {
  return userFacingError(
    error,
    'Throughline couldn’t complete that action. Please try again.',
  );
}
